import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Radar, Search, Loader2, AlertTriangle, Lock, CheckCircle2, XCircle,
  ArrowRight, Sparkles, TrendingUp,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { seoAnalyzePage, createLead, scoreTone, leadGenAudit } from "@/lib/api";
import { AuditReportEmail } from "@/components/AuditReportEmail";
import LeadGenReadiness from "@/components/LeadGenReadiness";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Tile = ({ label, value }) => {
  const t = scoreTone(value);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid={`audit-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-heading text-3xl font-extrabold" style={{ color: t.color }}>{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
};

export default function PublicAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [lgReport, setLgReport] = useState(null);
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const run = async () => {
    const u = url.trim();
    if (!u) return;
    setLoading(true); setError(""); setReport(null); setLgReport(null); setUnlocked(false);
    try {
      const [rep, lg] = await Promise.all([
        seoAnalyzePage(u),
        leadGenAudit(u, "leadgen-audit").catch(() => null),
      ]);
      setReport(rep);
      setLgReport(lg);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not analyze this URL. Please check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Please enter your name and email"); return; }
    setSubmitting(true);
    try {
      await createLead({
        name, email, website: report.final_url, service: "SEO + AI Visibility Audit",
        source: "seo-audit", website_score: report.scores.overall,
        message: `Free SEO+AI audit — ${report.opportunity_count} opportunities found. Classification: ${report.classification}.`,
      });
      setUnlocked(true);
      toast.success("Your full report is unlocked below");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 pb-24 pt-28 sm:px-6" data-testid="public-audit">
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-brand">
            <Radar size={13} /> 100% free · no signup to scan
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Free SEO + <span className="text-brand">AI Visibility</span> Audit
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-slate-500">
            See how your website performs across traditional search <em>and</em> AI-powered discovery — and exactly how to get found where people search today.
          </p>
        </div>

        <Card className="mx-auto mb-8 max-w-2xl border-slate-200 p-5">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="audit-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder="https://yourwebsite.com" className="h-12 pl-10 text-base" />
            </div>
            <Button data-testid="audit-run-btn" onClick={run} disabled={loading} className="btn-primary h-12 gap-2 px-6 text-base">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Radar size={18} />}
              {loading ? "Scanning…" : "Audit my site"}
            </Button>
          </div>
          {error && <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="audit-error"><AlertTriangle size={15} /> {error}</div>}
        </Card>

        {report && (
          <div className="space-y-6" data-testid="audit-report">
            {lgReport && <LeadGenReadiness report={lgReport} />}
            <Card className="border-slate-200 p-6">
              <div className="mb-1 text-center text-sm text-slate-400">{report.final_url}</div>
              <div className="mb-5 text-center font-heading text-2xl font-extrabold text-slate-900">
                We found <span className="text-brand">{report.opportunity_count}</span> opportunities to improve your website
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <Tile label="Overall" value={report.scores.overall} />
                <Tile label="SEO" value={report.scores.seo} />
                <Tile label="AI Visibility" value={report.scores.ai_visibility} />
                <Tile label="Content" value={report.scores.content} />
                <Tile label="Technical" value={report.scores.technical} />
                <Tile label="Mobile" value={report.scores.mobile} />
              </div>
            </Card>

            {!unlocked ? (
              <Card className="mx-auto max-w-lg border-2 border-brand/30 p-6 text-center" data-testid="audit-gate">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-brand"><Lock size={22} /></div>
                <div className="font-heading text-xl font-bold text-slate-900">Unlock your full action plan</div>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">Get every prioritized recommendation, your AI-visibility breakdown and the entities we detected — free.</p>
                <div className="mt-5 space-y-3">
                  <Input data-testid="audit-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11" />
                  <Input data-testid="audit-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" className="h-11" />
                  <Button data-testid="audit-unlock-btn" onClick={unlock} disabled={submitting} className="btn-primary h-11 w-full gap-2">
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Show my full report
                  </Button>
                </div>
              </Card>
            ) : (
              <>
                <AuditReportEmail report={report} brand="websitetech" />
                <Card className="border-slate-200 p-6" data-testid="audit-recommendations">
                  <div className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><TrendingUp size={18} className="text-brand" /> Your prioritized action plan</div>
                  <div className="space-y-3">
                    {report.recommendations.length === 0 && <div className="text-emerald-600">Great news — no critical issues found!</div>}
                    {report.recommendations.map((r, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 p-4" data-testid={`audit-rec-${i}`}>
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand">{r.priority} · {r.category}</div>
                        <div className="mt-1 font-semibold text-slate-900">{r.what}</div>
                        <div className="mt-1 text-sm text-slate-500">{r.recommendation}</div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="border-slate-200 p-6">
                  <div className="mb-3 font-heading font-bold text-slate-900">AI Visibility breakdown</div>
                  {report.ai_visibility_factors.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
                      {f.ok ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-slate-300" />}
                      <span className={f.ok ? "text-slate-700" : "text-slate-400"}>{f.factor}</span>
                    </div>
                  ))}
                </Card>

                <Card className="border-slate-200 bg-slate-900 p-8 text-center text-white" data-testid="audit-cta">
                  <div className="font-heading text-2xl font-extrabold">Want us to fix all {report.opportunity_count} of these for you?</div>
                  <p className="mx-auto mt-2 max-w-lg text-slate-300">Our team turns this report into a done-for-you optimization — SEO, AI visibility, schema, content and more.</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-3">
                    <Link to="/"><Button className="btn-primary h-11 gap-2 px-6">Talk to an expert <ArrowRight size={16} /></Button></Link>
                    <Link to="/seo"><Button variant="outline" className="h-11 gap-2 border-white/30 bg-white/10 px-6 text-white hover:bg-white/20">Explore the SEO engine</Button></Link>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
